import React, { useCallback, useEffect, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { useHistory, useParams } from "react-router-dom";
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ErrorValidationMessage } from './ErrorValidationMessage';
import api from '../api/api';
import '../layout/sass/_form.scss';
import { FormCampanhaMudas } from './FormCampanhaMudas';
import { TabelaCampanhaMudas } from './TabelaCampanhaMudas';
import { BrCalendar } from './BrCalendar';

export const FormCampanha = ({ isEditing, isLoading, defaultValues }) => {
    const { id } = useParams();
    const history = useHistory();
    const toast = useRef(null);
    const [isSubmiting, setIsSubmiting] = useState(false);
    const [mudas, setMudas] = useState([]);
    const [campanhaMudas, setCampanhaMudas] = useState([]);
    const [listMudas, setListMudas] = useState(null);

    // Função auxiliar para converter datas do JS em String local sem alteração de fuso horário (sem o "Z" UTC)
    const formatarDataLocal = (date) => {
        if (!date) return null;
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;

        const pad = (num) => String(num).padStart(2, '0');

        // Usa getFullYear, getMonth, getDate, getHours locais (não UTC)
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    const schema = Yup.object().shape({
        nome: Yup.string()
            .required("O nome é obrigatório")
            .max(200, "O nome deve ter no máximo 200 caracteres"),
        dataInicio: Yup.date().required("A data de início é obrigatória"),
        dataFim: Yup.date()
            .required("A data de término é obrigatória")
            .test("dataFim", "A data de término não pode ser anterior à data de início", function (value) {
                const { dataInicio } = this.parent;
                return !dataInicio || !value || value >= dataInicio;
            }),
        ativo: Yup.boolean(),
        qtde_mudas_rural: Yup
            .number()
            .required("A quantidade de mudas rural é obrigatória"),
        qtde_mudas_urbana: Yup
            .number()
            .required("A quantidade de mudas urbana é obrigatória"),
        endereco: Yup.string()
            .required("O endereço de entrega é obrigatório")
            .max(200, "O endereço deve ter no máximo 200 caracteres"),
    });

    const formik = useFormik({
        initialValues: defaultValues,
        validationSchema: schema,
        enableReinitialize: true,

        onSubmit: async (data) => {
            setIsSubmiting(true);

            // Formatação local estrita das datas para envio ao DTO no Spring Boot
            const payload = {
                ...data,
                dataInicio: formatarDataLocal(data.dataInicio),
                dataFim: formatarDataLocal(data.dataFim),
                dataEntrega: formatarDataLocal(data.dataEntrega)
            };

            try {
                let campanhaAtiva = await api.get(`campanha/ativo`);
                let campanhaAtivaId = { 'data': { "id": null } };
                if (campanhaAtiva.data === true) {
                    campanhaAtivaId = await api.get(`campanha/ativoCampanha`);
                }

                if (!isEditing) {
                    payload.muda = campanhaMudas;
                } else {
                    let mudasInicial = campanhaMudas;
                    let mudasFinal = payload.muda || [];

                    const mudasAtualizado = mudasFinal.map(item2 => {
                        const mesmaMuda = mudasInicial.find(item1 => item1.muda.id === item2.muda.id);
                        if (mesmaMuda) {
                            return {
                                ...item2,
                                quantidade: mesmaMuda.quantidade
                            };
                        }
                        return null;
                    }).filter(Boolean);

                    mudasInicial.forEach(item1 => {
                        const mesmaMuda = mudasFinal.find(item2 => item2.muda.id === item1.muda.id);
                        if (!mesmaMuda) {
                            const newItem = {
                                quantidade: item1.quantidade,
                                muda: item1.muda
                            };
                            mudasAtualizado.push(newItem);
                        }
                    });
                    payload.muda = mudasAtualizado;
                }

                if (!isEditing) {
                    if (formik.values.ativo === true && campanhaAtiva.data === true) {
                        toast.current.show({ life: 2000, severity: 'error', summary: 'Erro ao cadastrar!', detail: `Uma campanha já está ativa.` });
                    } else {
                        const dataMuda = (payload.muda || []).map((cm) => {
                            delete cm.saldo;
                            return cm;
                        });
                        payload.muda = dataMuda;
                        await api.post("campanha", payload);
                        formik.resetForm();
                        toast.current.show({ life: 2000, severity: 'success', summary: 'Cadastro concluído!', detail: `${payload.nome} adicionada com sucesso` });
                        setTimeout(() => {
                            history.push('/campanha');
                        }, 2000);
                    }
                } else {
                    if ((formik.values.ativo === true && campanhaAtiva.data === true) && payload.id !== campanhaAtivaId.data.id) {
                        toast.current.show({ life: 2000, severity: 'error', summary: 'Erro ao editar!', detail: `Outra campanha já está ativa.` });
                    } else {
                        const dataMuda = (payload.muda || []).map((cm) => {
                            if (!cm.hasOwnProperty('campanhaId')) {
                                cm.campanhaId = parseInt(id, 10);
                            }
                            delete cm.saldo;
                            return cm;
                        });
                        payload.muda = dataMuda;
                        await api.put("campanha", payload);
                        toast.current.show({ life: 2000, severity: 'success', summary: 'Cadastro atualizado!', detail: `${payload.nome} atualizada com sucesso` });
                        setTimeout(() => {
                            history.push('/campanha');
                        }, 2000);
                    }
                }
            } catch (error) {
                console.error("Erro ao salvar campanha:", error);
                toast.current.show({
                    life: 3000,
                    severity: 'error',
                    summary: isEditing ? 'Erro ao editar!' : 'Erro ao cadastrar!',
                    detail: error.response?.data || error.toString()
                });
            } finally {
                setIsSubmiting(false);
            }
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    useEffect(() => {
        if (isEditing && id) {
            api.get(`campanha/${id}`).then(async (response) => {
                formik.setValues(response.data);
                const tempMudaItens = (response.data.muda || []).map((i) => ({ id: i.id, nome: i.nome }));
                setMudas(tempMudaItens);

                const tempCampanhaMudas = await api.get(`campanhaMudas/saldo/${id}`);
                const muda = (tempCampanhaMudas.data || []).map((i) => ({
                    muda: i.campanhaMudas.muda,
                    quantidade: i.campanhaMudas.quantidade,
                    saldo: i.saldo
                }));

                setCampanhaMudas(muda);
            }).catch(e => console.error("Erro ao carregar dados da campanha:", e));
        }
    }, [id, isEditing]);

    const handleGetMudas = useCallback(async () => {
        try {
            const response = await api.get("mudas/mudasAll");
            const mudasApi = (response.data || []).map(item => ({
                id: item.id,
                nome: item.nome
            }));

            setListMudas(mudasApi);
            if (isEditing && formik.values.muda) {
                const IdsMudasCampanha = formik.values.muda.map(item => item.muda.id);
                const listaAtualizada = mudasApi.filter(item => !IdsMudasCampanha.includes(item.id));
                setListMudas(listaAtualizada);
            }
        } catch (e) {
            console.error("Erro ao carregar mudas:", e);
        }
    }, [formik.values.muda, isEditing]);

    useEffect(() => {
        handleGetMudas();
    }, [handleGetMudas]);

    return (
        <div className="card p-grid p-col-12 p-mx-0 p-mt-0">
            <Toast ref={toast} position="bottom-right" />
            <div className="p-col-12">
                <h2 className="p-mt-0">Campanha</h2>
                <form onSubmit={formik.handleSubmit}>
                    <div className="p-formgrid p-grid p-fluid p-mx-0">
                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="nome"
                                className={classNames({ 'p-error': isFormFieldValid('nome') })}
                            >
                                Nome
                            </label>
                            {!isLoading ? (
                                <InputText
                                    id="nome"
                                    name="nome"
                                    className={classNames({ 'p-invalid': isFormFieldValid('nome') })}
                                    value={formik.values.nome || ''}
                                    onChange={formik.handleChange}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.nome && formik.touched.nome &&
                                <ErrorValidationMessage message={formik.errors.nome} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4 p-md-4">
                            <label
                                htmlFor="dataInicio"
                                className={classNames({ 'p-error': isFormFieldValid('dataInicio') })}
                            >
                                Data de início
                            </label>
                            <BrCalendar
                                id="dataInicio"
                                name="dataInicio"
                                value={formik.values.dataInicio}
                                onChange={formik.handleChange}
                                showTime={true}
                                hourFormat="24"
                                className={classNames({ 'p-invalid': isFormFieldValid('dataInicio') })}
                                placeholder={isEditing && formik.values.dataInicio ? new Date(formik.values.dataInicio).toLocaleString('pt-BR') : ""}
                                yearRange={`1922:${new Date().getFullYear() + 3}`}
                            />
                            {formik.errors.dataInicio && formik.touched.dataInicio &&
                                <ErrorValidationMessage message={formik.errors.dataInicio} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4 p-md-4">
                            <label
                                htmlFor="dataFim"
                                className={classNames({ 'p-error': isFormFieldValid('dataFim') })}
                            >
                                Data de término
                            </label>
                            <BrCalendar
                                id="dataFim"
                                name="dataFim"
                                value={formik.values.dataFim}
                                onChange={formik.handleChange}
                                showTime={true}
                                hourFormat="24"
                                placeholder={isEditing && formik.values.dataFim ? new Date(formik.values.dataFim).toLocaleString('pt-BR') : ""}
                                className={classNames({ 'p-invalid': isFormFieldValid('dataFim') })}
                                yearRange={`1922:${new Date().getFullYear() + 3}`}
                            />
                            {formik.errors.dataFim && formik.touched.dataFim &&
                                <ErrorValidationMessage message={formik.errors.dataFim} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4 p-md-4">
                            <label
                                htmlFor="dataEntrega"
                                className={classNames({ 'p-error': isFormFieldValid('dataEntrega') })}
                            >
                                Data de entrega
                            </label>
                            <BrCalendar
                                id="dataEntrega"
                                name="dataEntrega"
                                value={formik.values.dataEntrega}
                                onChange={formik.handleChange}
                                showTime={true}            // Habilita a escolha de hora
                                hourFormat="24"            // Formato 24h
                                placeholder={isEditing && formik.values.dataEntrega ? new Date(formik.values.dataEntrega).toLocaleString('pt-BR') : ""}
                                className={classNames({ 'p-invalid': isFormFieldValid('dataEntrega') })}
                                yearRange={`1922:${new Date().getFullYear() + 3}`}
                            />
                            {formik.errors.dataEntrega && formik.touched.dataEntrega &&
                                <ErrorValidationMessage message={formik.errors.dataEntrega} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="qtde_mudas_rural"
                                className={classNames({ 'p-error': isFormFieldValid('qtde_mudas_rural') })}
                            >
                                Quantidade de mudas rural
                            </label>
                            {!isLoading ? (
                                <InputText
                                    id="qtde_mudas_rural"
                                    name="qtde_mudas_rural"
                                    type="number"
                                    className={classNames({ 'p-invalid': isFormFieldValid('qtde_mudas_rural') })}
                                    value={formik.values.qtde_mudas_rural || ''}
                                    onChange={formik.handleChange}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.qtde_mudas_rural && formik.touched.qtde_mudas_rural &&
                                <ErrorValidationMessage message={formik.errors.qtde_mudas_rural} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="qtde_mudas_urbana"
                                className={classNames({ 'p-error': isFormFieldValid('qtde_mudas_urbana') })}
                            >
                                Quantidade de mudas urbana
                            </label>
                            {!isLoading ? (
                                <InputText
                                    id="qtde_mudas_urbana"
                                    name="qtde_mudas_urbana"
                                    type="number"
                                    className={classNames({ 'p-invalid': isFormFieldValid('qtde_mudas_urbana') })}
                                    value={formik.values.qtde_mudas_urbana || ''}
                                    onChange={formik.handleChange}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.qtde_mudas_urbana && formik.touched.qtde_mudas_urbana &&
                                <ErrorValidationMessage message={formik.errors.qtde_mudas_urbana} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="endereco"
                                className={classNames({ 'p-error': isFormFieldValid('endereco') })}
                            >
                                Endereço de Entrega
                            </label>
                            {!isLoading ? (
                                <InputTextarea
                                    id="endereco"
                                    name="endereco"
                                    className={classNames({ 'p-invalid': isFormFieldValid('endereco') })}
                                    value={formik.values.endereco || ''}
                                    onChange={formik.handleChange}
                                    rows={5}
                                    cols={30}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.endereco && formik.touched.endereco &&
                                <ErrorValidationMessage message={formik.errors.endereco} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4 p-md-4" style={{ paddingTop: "25px" }}>
                            <label
                                htmlFor="ativo"
                                className={classNames({ 'p-error': isFormFieldValid('ativo') })}
                            >
                                Ativo
                            </label>
                            {!isLoading ? (
                                <Checkbox
                                    id="ativo"
                                    name="ativo"
                                    checked={!!formik.values.ativo}
                                    onChange={() => formik.setFieldValue('ativo', !formik.values.ativo)}
                                    className="p-ml-1"
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.ativo && formik.touched.ativo &&
                                <ErrorValidationMessage message={formik.errors.ativo} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4 p-md-4" style={{ paddingTop: "25px" }}>
                            <label
                                htmlFor="validarEnderecoPessoa"
                                className={classNames({ 'p-error': isFormFieldValid('validarEnderecoPessoa') })}
                            >
                                Validar Endereço?
                            </label>
                            {!isLoading ? (
                                <Checkbox
                                    id="validarEnderecoPessoa"
                                    name="validarEnderecoPessoa"
                                    checked={!!formik.values.validarEnderecoPessoa}
                                    onChange={() => formik.setFieldValue('validarEnderecoPessoa', !formik.values.validarEnderecoPessoa)}
                                    className="p-ml-1"
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.validarEnderecoPessoa && formik.touched.validarEnderecoPessoa &&
                                <ErrorValidationMessage message={formik.errors.validarEnderecoPessoa} />
                            }
                        </div>

                    </div>

                    <FormCampanhaMudas
                        campanhaMudas={campanhaMudas}
                        setCampanhaMudas={setCampanhaMudas}
                        mudas={mudas}
                        listMudas={listMudas}
                        setListMudas={setListMudas}
                    />

                    <br />

                    <div className="p-d-flex p-jc-end p-mr-1">
                        <TabelaCampanhaMudas
                            campanhaMudas={campanhaMudas}
                            setCampanhaMudas={setCampanhaMudas}
                            listMudas={listMudas}
                            setListMudas={setListMudas}
                        />
                    </div>

                    <div className="p-d-flex p-jc-end p-mr-1">
                        {!isLoading ? (
                            <Button
                                label={isEditing ? 'Atualizar' : 'Salvar'}
                                type="submit"
                                iconPos="right"
                                loading={isSubmiting}
                            />
                        ) : (
                            <Skeleton width="82.16px" height="35px" />
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};